import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    focusRealtimeWindow?: Window | null;
  }
}

type Resident = {
  id: number;
  name: string;
  room_number: string;
  role: string; // "resident" | "staff" | "guest"
};

export default function SearchPopup() {
  const [residents, setResidents] = useState<Resident[]>([]);
  const [selectedId, setSelectedId] = useState<number | "">("");
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const wsRef = useRef<WebSocket | null>(null);

  // Load residents (residents only, sorted)
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch("http://localhost:8000/get_residents", { cache: "no-store" });
        const data: Resident[] = await res.json();
        const onlyResidents = data.filter(r => (r.role || "").toLowerCase() === "resident");
        setResidents(onlyResidents.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (e) {
        console.error("Failed to load residents:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // WebSocket setup/cleanup
  useEffect(() => {
    const ws = new WebSocket("ws://localhost:8000/ws/realtime_search");
    wsRef.current = ws;

    ws.onopen = () => setStatusMsg("🟢 Connected. Select a resident to search…");

    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.match) {
          if (!window.focusRealtimeWindow || window.focusRealtimeWindow.closed) {
            window.focusRealtimeWindow = window.open(
              "http://localhost:3000/focus-realtime",
              "_blank",
              "width=1300,height=1000"
            );
          }
        }
      } catch (err) {
        console.error("Error parsing message:", err);
      }
    };

    ws.onerror = (e) => {
      console.error("WebSocket error:", e);
      setStatusMsg("❌ WebSocket error.");
    };

    ws.onclose = () => setStatusMsg("🔴 Disconnected.");

    return () => {
      try { ws.close(); } catch {}
      wsRef.current = null;
    };
  }, []);

  const handleSearch = () => {
    if (selectedId === "" || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    const selected = residents.find(r => r.id === selectedId);
    if (!selected) return;

    setStatusMsg(`🔎 Searching for ${selected.name}…`);
    wsRef.current.send(
      JSON.stringify({
        // keep "mode" if your backend expects it; otherwise you can omit
        mode: "name",
        resident_id: selected.id,
        name: selected.name, // optional for logs
      })
    );
  };

  return (
    <div className="min-h-screen bg-black text-white p-6 space-y-6">
      <h1 className="text-2xl font-bold">Real-Time Search</h1>

      <div className="border border-gray-600 bg-gray-900 p-4 rounded-lg">
        <p className="text-sm text-gray-300 leading-relaxed">
          Select a resident to start a real-time search. If a match is detected,
          a focused video window will open with their last known location.
        </p>
      </div>

      {/* Dropdown + button */}
      <div className="flex flex-col sm:flex-row gap-2">
        <select
          className="flex-1 bg-gray-800 border border-gray-600 p-2 rounded"
          value={selectedId}
          onChange={(e) => setSelectedId(Number(e.target.value) || "")}
          disabled={loading}
        >
          <option value="">
            {loading ? "Loading residents…" : "Select Resident"}
          </option>
          {residents.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}{r.room_number ? ` — Rm ${r.room_number}` : ""}
            </option>
          ))}
        </select>

        <button
          onClick={handleSearch}
          disabled={selectedId === "" || loading}
          className="px-4 py-2 bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          Start Search
        </button>
      </div>

      {statusMsg && <p className="text-gray-300">{statusMsg}</p>}
    </div>
  );
}
